import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { FirebasePerformanceTypes } from '@react-native-firebase/perf';
import { useStoreContext } from 'features/store';
import { normalizeAvailableDayParts } from 'features/store/utils/day-part-utils';
import { isEqual } from 'lodash-es';

import { IRestaurant } from '@rbi-ctg/store';
import { useLogMenuExperimentExposure } from 'experiments/menu-variant/use-log-menu-experiment-exposure';
import { useMenuExperimentVariant } from 'experiments/menu-variant/use-menu-experiment-variant-name';
import {
  IConfigOfferEntryDto,
  IRewardEntryDto,
  ISystemWideOfferEntryDto,
  useOneStoreMenuMetadataQuery,
} from 'generated/graphql-gateway';
import { useAppStateChangeListener } from 'hooks/use-app-state-change-listener';
import { useEnableUnifiedMenu } from 'hooks/use-enable-unified-menu';
import useInterval from 'hooks/use-interval';
import { DEFAULT_MENU_VARIANT_KEY, MENU_API_URL } from 'remote/constants';
import { LANGUAGES } from 'state/intl/types';
import { IDayPartBoundary, IValidDayPart } from 'state/menu/main-menu/types';
import { useDerivedServiceModeContext } from 'state/service-mode/derived';
import { appVersion } from 'utils/app-version-info';
import { getDateInTimezone } from 'utils/dateTime';
import { getLanguage } from 'utils/intl/language';
import { fetch } from 'utils/network';
import { startTrace } from 'utils/performance/trace';

import { findActiveDayParts, getDayPartsForDate } from './dayparts';
import {
  IIngredients,
  ILocalizedMenu,
  ILocalizedMenuEntry,
  IMainMenuNode,
  IndexedMenu,
  PosServiceMode,
} from './types';
import { IUnifiedMenuContext, UnifiedMenuContext } from './unified-menu-context';
import {
  createIndexedMenu,
  createIndexedOffers,
  createIndexedRewards,
  createMainMenuNode,
} from './utils';

interface ILoadedMenuVariant {
  requestedVariantKey: string;
  resolvedVariantKey: string;
  storeNumber: string | null;
}

interface IPayloadDayPartSource {
  menu: ILocalizedMenu | undefined;
  shouldRefreshActiveDayParts: boolean;
  storeNumber: string | undefined;
  timezone: string | undefined;
}

interface IPayloadDayPartValues {
  activeDayParts: IDayPartBoundary[];
  dayParts: IValidDayPart[];
}

interface IPayloadDayPartSnapshot extends IPayloadDayPartValues {
  source: IPayloadDayPartSource;
}

// Matches a JSON string literal's contents, handling escaped characters.
const JSON_STRING_VALUE = String.raw`((?:\\.|[^"\\])*)`;
const DAY_PART_REFRESH_INTERVAL = 60 * 1000;
const EMPTY_DAY_PARTS: IDayPartBoundary[] = [];
const EMPTY_MENU_DAY_PARTS: IValidDayPart[] = [];

const derivePayloadDayParts = ({
  menu,
  shouldRefreshActiveDayParts,
  timezone,
}: IPayloadDayPartSource): IPayloadDayPartValues => {
  if (!menu) {
    return {
      activeDayParts: EMPTY_DAY_PARTS,
      dayParts: EMPTY_MENU_DAY_PARTS,
    };
  }

  const now = timezone ? getDateInTimezone(timezone) : new Date();
  const menuDayParts = menu.dayParts ?? [];
  return {
    activeDayParts: shouldRefreshActiveDayParts
      ? findActiveDayParts(menuDayParts, now)
      : EMPTY_DAY_PARTS,
    dayParts: getDayPartsForDate(menuDayParts, now),
  };
};

// A plain `{"en":...,"fr":...}` pair (as used by e.g. `ingredients.name`/`components`), in
// either source order. Most localized-text fields (item/offer name, description, image altText)
// are NOT shaped like this — the gateway query (src/queries/gateway/menu.graphql) aliases their
// English field to `locale` at query time, so they arrive as `{"locale":...,"fr":...}` and never
// match these patterns.
const EN_THEN_FR = new RegExp(`"en":"${JSON_STRING_VALUE}","fr":"${JSON_STRING_VALUE}"`, 'g');
const FR_THEN_EN = new RegExp(`"fr":"${JSON_STRING_VALUE}","en":"${JSON_STRING_VALUE}"`, 'g');

/**
 * Rewrites a raw OneMenu JSON payload's localized-text keys to a single `"locale":"..."` key
 * per field, before parsing — avoids updating every `.locale` read in the app to be dynamic per
 * language.
 *
 * For plain `{"en":...,"fr":...}` fields, a blind single-key replace is order-dependent —
 * `JSON.parse` keeps the positionally-last duplicate `locale` key, not whichever replace ran
 * logically last — so it can pick the wrong language if French happens to appear before English
 * in the source. With French active, those pairs are resolved to their winning value explicitly
 * first, independent of source order.
 */
export const applyLocaleTextTransform = (text: string, activeLanguage: LANGUAGES): string => {
  if (activeLanguage !== LANGUAGES.fr) {
    return text.replaceAll(`"${LANGUAGES.en}":"`, '"locale":"');
  }

  const withPairsResolved = text
    .replace(EN_THEN_FR, (_match, en, fr) => `"locale":"${fr}"`)
    .replace(FR_THEN_EN, (_match, fr, en) => `"locale":"${fr}"`);

  return withPairsResolved
    .replaceAll(`"${LANGUAGES.fr}":"`, '"locale":"')
    .replaceAll(`"${LANGUAGES.en}":"`, '"locale":"');
};

/** Provides the active One Menu, including an assigned Amplitude menu variant when available. */
export const UnifiedMenuProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const isEnabled = useEnableUnifiedMenu();
  const { store, availableDayParts } = useStoreContext();
  const storeInfoActiveDayParts = normalizeAvailableDayParts(availableDayParts);
  const isMenuExperimentEnabled = isEnabled && Boolean(store?.number);
  const menuExperimentVariant = useMenuExperimentVariant({ isEnabled: isMenuExperimentEnabled });
  const isMenuExperimentPending = menuExperimentVariant?.isPending === true;
  const amplitudeVariantNames = isMenuExperimentPending
    ? []
    : menuExperimentVariant?.variantNames ?? [];
  const menuVariantNamesKey = amplitudeVariantNames.join('\u0000');
  const menuVariantKeys = useMemo(
    () => (menuVariantNamesKey ? menuVariantNamesKey.split('\u0000') : [DEFAULT_MENU_VARIANT_KEY]),
    [menuVariantNamesKey]
  );
  const menuVariantRequestKey = menuVariantKeys.join('\u0000');
  const menuVariantKey = menuVariantKeys[0];
  const { posServiceMode } = useDerivedServiceModeContext();
  const [hasError, setHasError] = useState(false);

  /**
   * Store the last fetch time for each store menu, used to determine if the menu if we need to refetch
   * the full menu or keep the cached version. This being empty means that the menu has never been fetched
   * in the current session.
   */
  const lastFetchTime = useRef<Record<string, number>>({});
  const fetchId = useRef(0);

  const [menu, setMenu] = useState<ILocalizedMenu | undefined>(undefined);
  const [loadedStoreNumber, setLoadedStoreNumber] = useState<string | null>(null);
  const [loadedMenuVariant, setLoadedMenuVariant] = useState<ILoadedMenuVariant>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [payloadDayPartSnapshot, setPayloadDayPartSnapshot] = useState<IPayloadDayPartSnapshot>();
  const isCurrentStoreMenuLoaded = Boolean(
    isEnabled &&
      !isMenuExperimentPending &&
      menu &&
      loadedStoreNumber === (store?.number ?? null) &&
      loadedMenuVariant?.requestedVariantKey === menuVariantRequestKey &&
      loadedMenuVariant.storeNumber === (store?.number ?? null)
  );
  const currentMenu = isCurrentStoreMenuLoaded ? menu : undefined;
  const isCurrentStoreMenuLoading = Boolean(
    isEnabled && store?.number && !isMenuExperimentPending && !hasError && !isCurrentStoreMenuLoaded
  );
  const metadataMenuVariantKey =
    loadedMenuVariant?.requestedVariantKey === menuVariantRequestKey &&
    loadedMenuVariant.storeNumber === (store?.number ?? null)
      ? loadedMenuVariant.resolvedVariantKey
      : menuVariantKey;

  // Get the last modified time of the menu and refetch if it has changed
  useOneStoreMenuMetadataQuery({
    variables: store?.number
      ? { storeId: store.number, variantName: metadataMenuVariantKey }
      : undefined,
    skip: !isEnabled || !store?.number || isMenuExperimentPending,
    onCompleted: data => {
      if (data.OneStoreMenuMetadata?.lastModified) {
        const lastModified = Date.parse(data.OneStoreMenuMetadata.lastModified);
        if (lastModified > lastFetchTime.current[store?.number ?? '']) {
          fetchMenu();
        }
      }
    },
  });

  const indexedMenu: IndexedMenu<ILocalizedMenuEntry> = useMemo(
    () => (currentMenu ? createIndexedMenu(currentMenu) : IndexedMenu.EMPTY),
    [currentMenu]
  );

  const indexedRewards: IndexedMenu<IRewardEntryDto> = useMemo(
    () => (currentMenu ? createIndexedRewards(currentMenu) : IndexedMenu.EMPTY),
    [currentMenu]
  );
  const indexedOffers: IndexedMenu<IConfigOfferEntryDto | ISystemWideOfferEntryDto> = useMemo(
    () => (currentMenu ? createIndexedOffers(currentMenu) : IndexedMenu.EMPTY),
    [currentMenu]
  );

  const shouldRefreshPayloadDayParts =
    isCurrentStoreMenuLoaded && storeInfoActiveDayParts === undefined;
  const payloadDayPartSource = useMemo<IPayloadDayPartSource>(
    () => ({
      menu: currentMenu,
      shouldRefreshActiveDayParts: shouldRefreshPayloadDayParts,
      storeNumber: store?.number ?? undefined,
      timezone: store?.timezone ?? undefined,
    }),
    [currentMenu, shouldRefreshPayloadDayParts, store?.number, store?.timezone]
  );
  const derivedPayloadDayParts = useMemo(() => derivePayloadDayParts(payloadDayPartSource), [
    payloadDayPartSource,
  ]);
  const isPayloadDayPartSnapshotCurrent = payloadDayPartSnapshot?.source === payloadDayPartSource;
  const payloadActiveDayParts = isPayloadDayPartSnapshotCurrent
    ? payloadDayPartSnapshot.activeDayParts
    : derivedPayloadDayParts.activeDayParts;
  const payloadDayParts = isPayloadDayPartSnapshotCurrent
    ? payloadDayPartSnapshot.dayParts
    : derivedPayloadDayParts.dayParts;

  const refreshPayloadDayParts = useCallback(() => {
    if (!payloadDayPartSource.menu) {
      return;
    }

    const nextPayloadDayParts = derivePayloadDayParts(payloadDayPartSource);
    setPayloadDayPartSnapshot(currentSnapshot => {
      const currentPayloadDayParts =
        currentSnapshot?.source === payloadDayPartSource ? currentSnapshot : derivedPayloadDayParts;
      if (
        isEqual(currentPayloadDayParts.activeDayParts, nextPayloadDayParts.activeDayParts) &&
        isEqual(currentPayloadDayParts.dayParts, nextPayloadDayParts.dayParts)
      ) {
        return currentSnapshot;
      }

      return {
        ...nextPayloadDayParts,
        source: payloadDayPartSource,
      };
    });
  }, [derivedPayloadDayParts, payloadDayPartSource]);
  useInterval(refreshPayloadDayParts, currentMenu ? DAY_PART_REFRESH_INTERVAL : null);
  const { addAppStateChangeListeners, removeAppStateChangeListeners } = useAppStateChangeListener();
  useEffect(() => {
    if (!currentMenu) {
      return;
    }

    addAppStateChangeListeners(refreshPayloadDayParts);
    return removeAppStateChangeListeners;
  }, [
    addAppStateChangeListeners,
    refreshPayloadDayParts,
    removeAppStateChangeListeners,
    currentMenu,
  ]);

  const activeDayParts = !isEnabled
    ? EMPTY_DAY_PARTS
    : storeInfoActiveDayParts ??
      (shouldRefreshPayloadDayParts ? payloadActiveDayParts : EMPTY_DAY_PARTS);
  const isDayPartsSourceResolved = Boolean(
    isEnabled &&
      store?.number &&
      (storeInfoActiveDayParts !== undefined || isCurrentStoreMenuLoaded)
  );

  const mainMenu = useMemo(
    () => maybeCreateMainMenu(currentMenu, indexedMenu, posServiceMode, store, activeDayParts),
    [activeDayParts, currentMenu, indexedMenu, posServiceMode, store]
  );
  const isExperimentVariantDisplayed = Boolean(
    isCurrentStoreMenuLoaded &&
      !isLoading &&
      mainMenu?.children.length &&
      menuExperimentVariant &&
      (amplitudeVariantNames.length > 0
        ? amplitudeVariantNames.includes(loadedMenuVariant?.resolvedVariantKey ?? '')
        : loadedMenuVariant?.resolvedVariantKey === DEFAULT_MENU_VARIANT_KEY)
  );
  useLogMenuExperimentExposure({
    exposureFlagKey: menuExperimentVariant?.exposureFlagKey,
    exposurePathname: menuExperimentVariant?.exposurePathname,
    isVariantDisplayed: isExperimentVariantDisplayed,
  });

  const fetchMenu = useCallback(async () => {
    let trace: FirebasePerformanceTypes.Trace | null = null;
    const currentFetchId = ++fetchId.current;
    const requestedStoreNumber = store?.number ?? null;
    try {
      setHasError(false);
      setIsLoading(true);

      // new high performance fetch that hits s3 directly which is pre BR compressed
      trace = startTrace(`fetch_1m_s3`, { storeId: store?.number ?? '' });
      const fetchMenuVariant = (variantKey: string): Promise<ILocalizedMenu> =>
        fetch<ILocalizedMenu>(`${MENU_API_URL}/${store?.number}-app-${variantKey}.json`, {
          method: 'GET',
          headers: {
            'x-ui-version': appVersion,
          },
          // unfortunately, this adds ~15ms in debug mode (less in release mode probably),
          // but it's better than updating every `.locale` in the app to be dynamic — see
          // applyLocaleTextTransform for why this isn't a single blind replace.
          textTransform: text => applyLocaleTextTransform(text, getLanguage()),
        });

      const variantKeys = new Set([...menuVariantKeys, DEFAULT_MENU_VARIANT_KEY]);
      let result: { menu: ILocalizedMenu; variantKey: string } | undefined;
      let lastError: unknown;

      for (const variantKey of variantKeys) {
        try {
          result = {
            menu: await fetchMenuVariant(variantKey),
            variantKey,
          };
          break;
        } catch (error) {
          if (currentFetchId !== fetchId.current) {
            return;
          }
          lastError = error;
        }
      }

      if (!result) {
        throw lastError;
      }

      // ignore stale responses from previous store changes
      if (currentFetchId !== fetchId.current) {
        return;
      }

      setMenu(result.menu);
      setLoadedStoreNumber(requestedStoreNumber);
      setLoadedMenuVariant({
        requestedVariantKey: menuVariantRequestKey,
        resolvedVariantKey: result.variantKey,
        storeNumber: requestedStoreNumber,
      });
      lastFetchTime.current[requestedStoreNumber ?? ''] = Date.now();
    } catch (error) {
      if (currentFetchId !== fetchId.current) {
        return;
      }
      setHasError(true);
      setMenu(undefined);
      setLoadedStoreNumber(null);
      setLoadedMenuVariant(undefined);
    } finally {
      trace?.stop();
      if (currentFetchId === fetchId.current) {
        setIsLoading(false);
      }
    }
  }, [menuVariantKeys, menuVariantRequestKey, store?.number]);

  useEffect(() => {
    if (isEnabled && store?.number && !isMenuExperimentPending) {
      fetchMenu();
    }
  }, [isEnabled, isMenuExperimentPending, store?.number, fetchMenu]);

  useEffect(() => {
    if (!isEnabled || isMenuExperimentPending) {
      // Invalidate a pending One Menu request so it cannot repopulate state while this
      // provider is inactive or the menu experiment assignment is unresolved.
      fetchId.current += 1;
      setIsLoading(false);
    }
    setMenu(undefined);
    setLoadedStoreNumber(null);
    setLoadedMenuVariant(undefined);
    setHasError(false);
  }, [isEnabled, isMenuExperimentPending, menuVariantRequestKey, store?.number]);

  const value: IUnifiedMenuContext = {
    activeDayParts,
    dayParts: currentMenu ? payloadDayParts : EMPTY_MENU_DAY_PARTS,
    rawMenu: currentMenu,
    mainMenu: mainMenu?.children ?? EMPTY_MENU,
    indexedMenu,
    indexedRewards,
    indexedOffers,
    loadedStoreNumber: isCurrentStoreMenuLoaded ? loadedStoreNumber : null,
    isDayPartsSourceResolved,
    isLoading: isLoading || isMenuExperimentPending || isCurrentStoreMenuLoading,
    refetchMenu: fetchMenu,
    ingredients: (currentMenu?.ingredients as IIngredients[]) ?? [],
    hasError,
  };

  return <UnifiedMenuContext.Provider value={value}>{children}</UnifiedMenuContext.Provider>;
};

const EMPTY_MENU: IMainMenuNode[] = [];

function maybeCreateMainMenu(
  menu: ILocalizedMenu | undefined,
  indexedMenu: IndexedMenu<ILocalizedMenuEntry> | undefined,
  posServiceMode: PosServiceMode | null,
  store: IRestaurant | undefined,
  activeDayParts: IDayPartBoundary[]
): IMainMenuNode | undefined {
  if (indexedMenu && menu && posServiceMode && store) {
    return createMainMenuNode(indexedMenu, menu.rootMenuEntryId, {
      posServiceMode,
      dayPartIds: activeDayParts.map(dayPart => dayPart.key),
    });
  }
  return undefined;
}
